import {
  pathOr,
  filter,
  cond,
  pipe,
  always,
  T,
  set,
  lensPath,
  map,
  curry,
  includes,
} from 'ramda';
import { ifToAddOrRemoveThisIntentWhenAllPass } from 'src/features/core/ChatEngine/Engine/deactivatedUpon';
import { mkAPIUpdateableForArrayType } from 'src/features/core/ChatEngine/Bot/botServiceUtils/arrayTypeHandler';
import {
  shouldAPIToBeUpdated,
  mkPromiseDispatchersForAPIUpdateable,
  dataFeederForCommonDataType,
} from 'src/features/core/ChatEngine/Bot/botServiceUtils/commonFns';
import { mkAPIUpdateableForPathType } from 'src/features/core/ChatEngine/Bot/botServiceUtils/pathTypeHandler';
import { isNotEmpty } from 'src/utilities/hasValue';
import { compose } from 'recompose';
import { LENDSMART_SH_API_RESOURCE_WRITE } from 'src/features/core/Intents/intentConstants';

//////////////////  PUBLIC FUNCTIONS ////////////////////////

/**
 * wipeOutDependentAnswer method which handle the process of API data saved for
 * different intents based on their dependent intent
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @returns updated dataBag JSON which will be use for next Process in intents cycle
 */
export const wipeOutDependentAnswer = (dataBag: any) => {
  const _removeThisFields = removeThisIntentDatasFromAPI(dataBag);
  return cond([
    [isNotEmpty, curry(wipeOutIntentsAnswerFromAPI)(dataBag)],
    [T, always(dataBag)],
  ])(_removeThisFields);
};

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 *findDependentIntentIfNeedsToBeRemoved - Check whether the dependent intents needs to be removed or not,
 if needs to be removed then it returns true
 * @param intent single Intent which needs to be check for to remove or not
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @returns boolean(true or false)
 */
const findDependentIntentIfNeedsToBeRemoved = (dataBag: any, intent: any) => {
  const { answer } = dataBag;
  return pathOr(
    false,
    ['remove'],
    ifToAddOrRemoveThisIntentWhenAllPass(
      pathOr([], ['de_activated_upon'], intent),
      [],
      answer,
      "",
      [],
      pathOr({}, ["loanApp"],dataBag.storeState),
    ),
  );
};

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * getRemovalDependentIntents - method that returns the list of intent that
 * needs to have a removal process
 * @param siteIntents contains list of intent that is from configurations
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @returns the list of intents that needs to be removed by having a check
 * whether the intent needs to be removed or not
 */
const getRemovalDependentIntents = (siteIntents: any, dataBag: any) =>
  filter(curry(findDependentIntentIfNeedsToBeRemoved)(dataBag), siteIntents);

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * checkTheKeyFields - method that checks the key field of that particular intent and the intent
 * which has this key field in their dependent conditions
 * @param singleSiteIntent contains single intent that is from configurations
 * @param deactivateCondition contains single deactivatingCondition of that intent
 * @returns boolean(true or false)
 */
const checkTheKeyFields = (singleSiteIntent: any, deactivateCondition: any) =>
  includes(pathOr('', ['key_field'], singleSiteIntent), [
    pathOr('', ['equals', 'key_field'], deactivateCondition),
    pathOr('', ['lt', 'key_field'], deactivateCondition),
    pathOr('', ['lte', 'key_field'], deactivateCondition),
    pathOr('', ['gte', 'key_field'], deactivateCondition),
    pathOr('', ['gt', 'key_field'], deactivateCondition),
  ]);

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * IsToRemoveDependentIntents - Method returns conditions which sattisifies for the remove conditions of that particular intent
 * @param deactivateConditions contians list of deactivating conditions for particular intent
 * @param singleSiteIntent contains a single intent from intent config
 * @returns list of conditions which sattisifies with remove conditions of that particular intent
 */
const IsToRemoveDependentIntents = (
  deactivateConditions: any,
  singleSiteIntent: any
) => filter(curry(checkTheKeyFields)(singleSiteIntent), deactivateConditions);

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * removeDependentIntentIfThere - removes the intent by checking the key field is in
 * their deactivating conditions
 * @param singleSiteIntent contains a single intent from intent config
 * @param intent intent JSON which is needs to be removed
 * @returns boolean(true or false)
 */
const removeDependentIntentIfThere = (singleSiteIntent: any, intent: any) =>
  isNotEmpty(
    IsToRemoveDependentIntents(
      pathOr([], ['de_activated_upon'], intent),
      singleSiteIntent
    )
  );

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * removeThisIntentDatasFromAPI - method which process the list of intents
 * whose API saved data needs to be removed
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @returns list of intents which is to be removed with their API data
 */
const removeThisIntentDatasFromAPI = (dataBag: any) => {
  const { siteIntents, singleSiteIntent } = dataBag;
  return filter(
    curry(removeDependentIntentIfThere)(singleSiteIntent),
    getRemovalDependentIntents(siteIntents, dataBag)
  );
};

//////////////////  PRIVATE FUNCTIONS ////////////////////////

const mkAlterationForDataBag = (
  dataBag: any,
  valueForDataBagAlteration: any
) => {
  return compose(
    set(
      lensPath(['filterArrayTypeData']),
      pathOr([], ['filterArrayTypeData'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['filterPathTypeData']),
      pathOr([], ['filterPathTypeData'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['apiData']),
      pathOr({}, ['apiData'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['apiResource']),
      pathOr([], ['apiResource'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['_questionId']),
      pathOr('', ['question_id'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['mappingKeys']),
      pathOr([], ['mappingKeys'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['requests']),
      pathOr([], ['requests'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['_apiResource']),
      pathOr({}, ['_apiResource'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['answer']),
      pathOr({}, ['answer'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['saveInThisPaths']),
      pathOr({}, ['saveInThisPaths'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['answerObject']),
      pathOr({}, ['answerObject'], valueForDataBagAlteration)
    ),
    set(
      lensPath(['arrayOfAnswerObject']),
      pathOr({}, ['arrayOfAnswerObject'], valueForDataBagAlteration)
    )
  )(dataBag);
};

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * wipeOutIntentsAnswerFromAPI method which removed intents response from API for the respective intents
 * @param dataBagAsParams consists of required datas inside it which intiated in SimpleBotService File
 * @param intentsToBeRemoved - consists of intents which is to be removed from API
 * @returns updated dataBag for next process of intent update cycle
 */
const wipeOutIntentsAnswerFromAPI = (
  dataBagAsParams: any,
  intentsToBeRemoved: any
) => {
  let dataBag = dataBagAsParams;
  const removalProcessForThisIntent = (intent: any) => {
    const { apiStateStore, apiData, requests, apiResource } = dataBag;
    const {
      filterArrayTypeData,
      filterPathTypeData,
      _apiResource,
      mappingKeys,
      saveInThisPaths,
    } = dataFeederForCommonDataType(apiStateStore, intent);
    const readResource = pathOr(
      '',
      ['metadata', LENDSMART_SH_API_RESOURCE_WRITE],
      intent
    );
    dataBag = mkAlterationForDataBag(dataBag, {
      filterArrayTypeData,
      filterPathTypeData,
      apiData,
      apiResource,
      question_id: pathOr('', ['question_id'], intent),
      mappingKeys,
      requests,
      _apiResource,
      saveInThisPaths,
      answer: '',
      answerObject: {},
      arrayOfAnswerObject: [],
    });
    const updatedDataBag = cond([
      [() => !shouldAPIToBeUpdated(intent), () => dataBag],
      [
        isNotEmpty,
        () =>
          pipe(mkAPIUpdateableForArrayType, (_dataBag: any) =>
            mkPromiseDispatchersForAPIUpdateable(_dataBag, { readResource })
          )(dataBag),
      ],
      [
        T,
        () =>
          pipe(mkAPIUpdateableForPathType, (_dataBag: any) =>
            mkPromiseDispatchersForAPIUpdateable(_dataBag, { readResource })
          )(dataBag),
      ],
    ])(filterArrayTypeData);
    dataBag = updatedDataBag;
    return dataBag;
  };
  map(removalProcessForThisIntent, intentsToBeRemoved);
  dataBag = mkAlterationForDataBag(dataBag, {
    filterArrayTypeData: pathOr('', ['filterArrayTypeData'], dataBagAsParams),
    filterPathTypeData: pathOr('', ['filterPathTypeData'], dataBagAsParams),
    apiData: pathOr({}, ['apiData'], dataBag),
    apiResource: pathOr('', ['apiResource'], dataBagAsParams),
    question_id: pathOr('', ['currenQuestionId'], dataBagAsParams),
    mappingKeys: pathOr('', ['mappingKeys'], dataBagAsParams),
    requests: pathOr('', ['requests'], dataBag),
    _apiResource: pathOr('', ['_apiResource'], dataBagAsParams),
    saveInThisPaths: pathOr('', ['saveInThisPaths'], dataBagAsParams),
    answer: pathOr('', ['answer'], dataBagAsParams),
    answerObject: {},
    arrayOfAnswerObject: [],
  });
  return dataBag;
};
